import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, Share2, FileText } from 'lucide-react'
import { fetchBlogPost } from '../utils/api'
import type { BlogPost } from '../types'

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!slug) return
    
    fetchBlogPost(slug)
      .then(setPost)
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [slug])

  useEffect(() => {
    if (post) {
      // Update page title
      document.title = `${post.title} | DocSigner`
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]')
      if (metaDescription) {
        metaDescription.setAttribute('content', post.excerpt)
      }
      
      // Update Open Graph tags
      const ogTitle = document.querySelector('meta[property="og:title"]')
      if (ogTitle) {
        ogTitle.setAttribute('content', post.title)
      }
      
      const ogDescription = document.querySelector('meta[property="og:description"]')
      if (ogDescription) {
        ogDescription.setAttribute('content', post.excerpt)
      }
      
      const ogUrl = document.querySelector('meta[property="og:url"]')
      if (ogUrl) {
        ogUrl.setAttribute('content', window.location.href)
      }
      
      // Update Twitter tags
      const twitterTitle = document.querySelector('meta[name="twitter:title"]')
      if (twitterTitle) {
        twitterTitle.setAttribute('content', post.title)
      }
      
      const twitterDescription = document.querySelector('meta[name="twitter:description"]')
      if (twitterDescription) {
        twitterDescription.setAttribute('content', post.excerpt)
      }
      
      // Update canonical URL
      let canonical = document.querySelector('link[rel="canonical"]')
      if (canonical) {
        canonical.setAttribute('href', window.location.href)
      } else {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        canonical.setAttribute('href', window.location.href)
        document.head.appendChild(canonical)
      }
    }
    
    // Cleanup: restore default title when component unmounts
    return () => {
      document.title = 'Minuta Cessar Contrato de Seguro | Modelo Cessação Contrato Seguro Online'
    }
  }, [post])

  const handleShare = async () => {
    if (!post) return
    if (navigator.share) {
      await navigator.share({
        title: post.title,
        url: window.location.href,
      })
    } else {
      await navigator.clipboard.writeText(window.location.href)
      alert('Link copiado!')
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Artigo não encontrado</h1>
          <p className="text-gray-500 mb-4">O artigo que procura não existe.</p>
          <Link to="/blog" className="btn-primary inline-flex">
            Ver todos os artigos
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 py-8 px-4">
      <article className="max-w-3xl mx-auto">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-primary-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar ao blog
        </Link>

        <header className="mb-8">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-4">
            {post.category}
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString('pt-PT', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.read_time} leitura
            </span>
            <button
              onClick={handleShare}
              className="flex items-center gap-1 hover:text-primary-600 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Partilhar
            </button>
          </div>
        </header>

        <div 
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <footer className="mt-12 pt-8 border-t border-gray-200">
          <div className="bg-gray-50 rounded-2xl p-6 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Precisa de resolver um contrato de seguro?
            </h3>
            <p className="text-gray-600 mb-4">
              Gere a sua minuta de resolução gratuitamente em segundos.
            </p>
            <Link to="/" className="btn-primary inline-flex">
              Gerar Minuta Agora
            </Link>
          </div>
        </footer>
      </article>
    </div>
  )
}
